import React from "react";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Grid,
  Typography,
} from "@tiny/components";
import { CustomerInfoView } from "@tiny/api";

export interface UtmDetailsDialogProps {
  open: boolean;
  customer?: CustomerInfoView;
  onClose: () => void;
}

const EMPTY_VALUE = "-";

function isEmpty(value: any) {
  return value === null || value === undefined || value === "";
}

// Shape below the section level isn't guaranteed, so this stays generic
// instead of assuming specific keys (e.g. first_touch/last_touch).
function UtmFields({
  value,
  isTopLevel = false,
}: {
  value: any;
  isTopLevel?: boolean;
}) {
  if (isEmpty(value)) {
    return (
      <Typography variant="body2" color="grey" tone={500}>
        {EMPTY_VALUE}
      </Typography>
    );
  }
  if (typeof value !== "object") {
    return (
      <Typography variant="body2" color="grey" tone={900}>
        {String(value)}
      </Typography>
    );
  }

  const entries = Array.isArray(value)
    ? value.map((v, i) => [String(i), v] as const)
    : Object.entries(value);

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="grey" tone={500}>
        {EMPTY_VALUE}
      </Typography>
    );
  }

  if (isTopLevel) {
    return (
      <Grid container spacing={4}>
        {entries.map(([key, entryValue]) => (
          <Grid item xs={6} key={key}>
            <Typography
              variant="label1"
              color="grey"
              tone={600}
              display="block"
              mb={1}
            >
              {key}
            </Typography>
            {typeof entryValue === "object" && entryValue !== null ? (
              <UtmFields value={entryValue} />
            ) : (
              <Typography variant="body2" color="grey" tone={900}>
                {isEmpty(entryValue) ? EMPTY_VALUE : String(entryValue)}
              </Typography>
            )}
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Box pl={4}>
      {entries.map(([key, entryValue]) => (
        <Box key={key} mb={2}>
          <Typography variant="label1" color="grey" tone={600} display="block">
            {key}
          </Typography>
          <UtmFields value={entryValue} />
        </Box>
      ))}
    </Box>
  );
}

function UtmSection({ label, value }: { label: string; value: any }) {
  return (
    <Box mb={4} p={4} style={{ background: "#F7F8FA", borderRadius: 8 }}>
      <Typography
        variant="body1"
        fontWeight="bold"
        color="grey"
        tone={900}
        display="block"
        mb={3}
      >
        {label}
      </Typography>
      <UtmFields value={value} isTopLevel />
    </Box>
  );
}

function UtmDetailsDialog(props: UtmDetailsDialogProps) {
  const { open, onClose, customer } = props;
  const utm = customer?.utm;
  const utmEntries = utm ? Object.entries(utm) : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableScrollLock
    >
      <DialogTitle onClose={onClose}>
        UTM details {customer?.email ? `— ${customer.email}` : ""}
      </DialogTitle>
      <DialogContent>
        {utmEntries.length > 0 ? (
          utmEntries.map(([key, value]) => (
            <UtmSection key={key} label={key} value={value} />
          ))
        ) : (
          <Typography variant="body2" color="grey" tone={500}>
            No UTM data captured for this contact.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UtmDetailsDialog;
